import { Trans } from '@lingui/react/macro';
import { SheetTrigger } from '@repo/videa-components/src/index';
import { differenceInYears } from 'date-fns';
import { useSelector } from 'react-redux';

import { useGetDateFNSLocale } from '../../../../../locales/helpers';
import type { Appointment } from '../../../../../react-query/useAppointments';
import { parseAndFormatDate, parseDate } from '../../../../../utils/parseDate';
import {
  type PatientListPageLayoutContext,
  PatientListPageLayoutContextMap,
} from '../../../types';
import { AppointmentPatientNameGuard } from '../AppointmentPatientNameGuard';

export const getPatientAge = (simpleDOB?: string) => {
  if (!simpleDOB) return null;
  const today = new Date();
  return differenceInYears(today, parseDate(simpleDOB));
};

interface PatientInfoProps {
  appointment: Appointment;
  index: number;
  context?: PatientListPageLayoutContext;
  isClickable?: boolean;
  onClick?: () => void;
}

export const PatientInfo = ({
  appointment,
  index,
  context,
  isClickable = false,
  onClick,
}: PatientInfoProps) => {
  const privacyOn = useSelector((state: any) => state.login?.user?.privacyOn);

  const dateFNSLocale = useGetDateFNSLocale();
  const patientDOB = appointment.patient?.dob;

  const formattedDOB = parseAndFormatDate(patientDOB, undefined, dateFNSLocale);
  const sourcePatientId = appointment.patient?.sourcePatientId;

  const age = getPatientAge(patientDOB);

  return (
    <div
      className={`flex gap-4 p-4 items-start min-w-[12em] vai-transition-colors ${
        isClickable && privacyOn ? 'hover:[&_*]:!vai-text-blue-600' : ''
      }`}
      data-testid="patient-row"
    >
      <div className="flex flex-col items-start gap-2 p-0 h-fit flex-grow">
        {/* sheet trigger is a part of Sheet element from ShadCN
          https://ui.shadcn.com/docs/components/sheet (see Sheet element at
          PatientList) */}
        {context === PatientListPageLayoutContextMap.Huddle ? (
          <SheetTrigger onClick={onClick}>
            <span className="vai-flex">
              <AppointmentPatientNameGuard
                context={context}
                appointment={appointment}
                index={index}
              />
            </span>
          </SheetTrigger>
        ) : (
          <span className="vai-flex">
            <AppointmentPatientNameGuard
              context={context}
              appointment={appointment}
              index={index}
            />
          </span>
        )}

        {
          <div
            className={`flex items-center vai-mt-0 ${
              context === PatientListPageLayoutContextMap.Claims
                ? ''
                : 'vai-mb-2'
            }`}
          >
            <span
              className="vai-text-sm vai-font-thin"
              data-testid="patient-age-and-dob"
            >
              {(patientDOB || sourcePatientId) &&
              (context === PatientListPageLayoutContextMap.Huddle ||
                context === PatientListPageLayoutContextMap.Ambient) ? (
                <span>
                  {privacyOn ? (
                    <span>
                      {patientDOB && (
                        <>
                          <Trans>Age</Trans> {age} | <Trans>DOB</Trans>:{' '}
                          {formattedDOB}
                        </>
                      )}
                      {sourcePatientId && (
                        <span>
                          {patientDOB && ' | '}
                          <Trans>Patient ID</Trans>: {sourcePatientId}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="vai-text-gray-300 vai-blur-[5px] vai-select-none vai-font-mono vai-text-sm vai-font-thin">
                      &#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;&#x2588;
                    </span>
                  )}
                </span>
              ) : null}
            </span>
          </div>
        }
      </div>
    </div>
  );
};
